import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../../api/entityType";
import useToDateEndingIsYesterday from "../../../hooks/useToDateEndingIsYesterday";
import { transformAuditInput } from "../../../store/inventory/inventoryActions";
import {
  clearOperationResult,
  exportReport,
  fetchReport,
  resetReportState,
} from "../../../store/report/reportActions";
import PermissionWrapper from "../../navigation/PermissionWrapper";
import ReportPanel from "../../shared/ReportPanel";
import AgencySelectEx from "../../shared/form/AgencySelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../../shared/form/fixedDateRanges";
import { getInitialAgency } from "../../shared/form/getInitialAgency";
import AdvancedRangePicker from "../../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toEndOfDay, toStartOfDay } from "../../shared/formatting";
import openNotificationOperationResult from "../../shared/openNotificationOperationResult";
import { ManageInventory } from "../../shared/securityRoleTasks";
import { agenciesListEqual } from "../../shared/sorting";
import { inventoryAuditReportSchema } from "./reportSchemas";

// Constants
const partialInitialValues = {
  AgencyKey: "",
  FixedDateRange: fixedDateRanges.MTD,
  TransactionDate: ["", ""],
};

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 18 },
};

const InventoryAuditReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(inventoryAuditReportSchema),
  });

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const isExporting = useSelector((state) => state.report.isExporting);
  const toDateEndingIsYesterday = useToDateEndingIsYesterday();
  const reportOperationResult = useSelector(
    (state) => state.report.operationResult
  );

  //
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKey: initialAgency,
        TransactionDate: [
          toStartOfDay(dayjs().startOf("month")),
          toEndOfDay(dayjs().endOf("day")),
        ],
      };

      setInitialDefaultValues(cloneDeep(fullInitialValues));
      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  useEffect(() => {
    openNotificationOperationResult(reportOperationResult, () =>
      dispatch(clearOperationResult())
    );
  }, [reportOperationResult, dispatch]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformAuditInput(values),
        entityType.INVENTORY,
        "PrintAudit",
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformAuditInput(getValues()),
          "InventoryAuditReport",
          entityType.INVENTORY
        )
      ),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid || isExporting,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="AuditInventoryReport">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ManageInventory}
      >
        <Row>
          <Col span={8}>
            <AgencySelectEx
              layout={layout}
              formLabel="Agency"
              name="AgencyKey"
              required={true}
              formStyle={{ marginBottom: 0 }}
              agencies={currentAgencies}
              isLoading={defaultValues.AgencyKey === ""}
              control={control}
            />
          </Col>
          <Col span={12}>
            <AdvancedRangePicker
              formItem={{
                labelCol: {
                  span: 6,
                },
                wrapperCol: {
                  span: 18,
                },
                required: true,
                label: "Transaction Date",
              }}
              control={control}
              setValue={setValue}
              allowClear={true}
              name="TransactionDate"
              format={dateFormat}
              dateRangeConfig={dateRangeConfigs.TRANSACTION}
              toDateEndingIsYesterday={toDateEndingIsYesterday}
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default InventoryAuditReport;
